import React from 'react';
import { ClockIcon, RefreshCwIcon, LogOutIcon } from 'lucide-react';
import { theme } from '../config/theme';

interface SessionWarningProps {
  show: boolean;
  countdown: number;
  isExpired: boolean;
  onExtend: () => void;
  onLogout: () => void;
}

const SessionWarning: React.FC<SessionWarningProps> = ({
  show,
  countdown,
  isExpired,
  onExtend,
  onLogout
}) => {
  if (!show) return null;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-center w-12 h-12 rounded-full mx-auto mb-4" style={{ backgroundColor: theme.colors.primary[100] }}>
          <ClockIcon className="h-6 w-6" style={{ color: theme.colors.primary[600] }} />
        </div>
        
        <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
          {isExpired ? 'Session Expired' : 'Session Timeout Warning'}
        </h3>
        
        <p className="text-center text-gray-600 mb-6">
          {isExpired 
            ? 'Your session has expired due to inactivity. Please log in again.'
            : `Your session will expire in ${formatTime(countdown)} due to inactivity.`
          }
        </p>
        
        {!isExpired && (
          <div className="rounded-md p-3 mb-4" style={{ backgroundColor: theme.colors.primary[50], borderColor: theme.colors.primary[200], borderWidth: '1px' }}>
            <p className="text-sm" style={{ color: theme.colors.primary[800] }}>
              <strong>Tip:</strong> Any interaction with the website will automatically extend your session.
            </p>
          </div>
        )}
        
        <div className="flex space-x-3">
          {!isExpired ? (
            <>
              <button
                onClick={onExtend}
                style={{ backgroundColor: theme.colors.primary[800] }}
                className="flex-1 text-white px-4 py-2 rounded-md hover:opacity-90 transition-colors flex items-center justify-center"
              >
                <RefreshCwIcon className="h-4 w-4 mr-2" />
                Extend Session
              </button>
              <button
                onClick={onLogout}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors flex items-center justify-center"
              >
                <LogOutIcon className="h-4 w-4 mr-2" />
                Log Out
              </button>
            </>
          ) : (
            <button
              onClick={onLogout}
              className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors flex items-center justify-center"
            >
              <LogOutIcon className="h-4 w-4 mr-2" />
              Log Out
            </button>
          )}
        </div>
        
        {!isExpired && (
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="h-2 rounded-full transition-all duration-1000 ease-linear"
                style={{ backgroundColor: theme.colors.primary[600], width: `${(countdown / 60) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionWarning;
